// Collapses repeat reads of the shared reference collections.
//
// `users`, `lots`, `admins` and `paymentDays` are read into Redux once by the
// app shell on boot, and then read *again* by every page that happens to need
// them: `getAllLots` alone ran six times in a single session (app.jsx plus
// ordersLots, lotlist, deliveryLots, communication/lotList and invoice), and
// `getAllUsers` three times. Each of those is an unbounded collection read, so
// at this dataset's size the duplicates cost seconds and a large slice of the
// Firestore read bill for data the store already had.
//
// Two guards, deliberately simple:
//   - `dedupe` shares one in-flight request between concurrent callers, so a
//     page mounting while the boot preload is still running joins that request
//     instead of starting a second one.
//   - `isFresh` lets a thunk skip the round trip entirely when the data was
//     loaded moments ago.
//
// The TTL is short on purpose. Local mutations don't depend on it — the
// reducers already fold an upload/update/delete into the store, so skipping a
// refetch never hides the admin's own change — it only bounds how long another
// admin's change can take to appear, which a minute covers comfortably.

const inFlight = new Map();
const completedAt = new Map();

export const FRESH_FOR_MS = 60 * 1000;

export const isFresh = (key, ttl = FRESH_FOR_MS) => {
  const at = completedAt.get(key);
  return typeof at === "number" && Date.now() - at < ttl;
};

export const dedupe = (key, loader) => {
  const pending = inFlight.get(key);
  if (pending) return pending;

  const promise = Promise.resolve()
    .then(loader)
    .then((result) => {
      // Stamped only on success, so a failed read is retried rather than
      // treated as a fresh empty result.
      completedAt.set(key, Date.now());
      return result;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise;
};

// For callers that must see server state right now (e.g. after a write whose
// result isn't folded into the store).
export const invalidate = (key) => {
  completedAt.delete(key);
};
